'use client'
import { NexaBlack } from '@/styles/fonts'
import { giphyBlack, giphyBlue, giphyDarkestGrey, giphyLightestGrey } from '@giphy/colors'
import { useEffect, useState } from 'react'
import useLocalStorage from 'react-use/lib/useLocalStorage'
import styled, { keyframes } from 'styled-components'
import IconButton from 'ui/src/components/controls/icon-button'

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`

const Banner = styled.div<{ $fadingOut: boolean; $showIt: boolean }>`
    height: ${({ $showIt: $delay }) => ($delay ? 108 : 0)}px;
    transition: height 0.2s;
    margin: 20px 0px;
    display: flex;
    flex-direction: row;
    background-color: ${giphyDarkestGrey};
    overflow: hidden;
    border-radius: 6px;
    opacity: ${({ $fadingOut }) => ($fadingOut ? 0 : 1)};
    animation: ${({ $fadingOut }) => ($fadingOut ? fadeOut : 'none')} 0.5s forwards;
    & h1 {
        font-family: ${NexaBlack.style.fontFamily};
        font-size: 20px;
        margin-right: 10px;
    }
    & p {
        white-space: pre-line;
        margin-top: 5px;
        line-height: 22px;
        letter-spacing: 0.23px;
        color: ${giphyLightestGrey};
    }
    & a {
        font-weight: bold;
        color: ${giphyBlue};
    }
`

const ColorBar = styled.div`
    background: linear-gradient(45deg, rgb(0, 255, 153) 0%, rgb(0, 172, 215) 100%);
    border-radius: 6px 0px 0px 6px;
    height: 108px;
    width: 8px;
`

const Info = styled.div`
    width: 675px;
    flex-direction: column;
    margin: 16px 0px 19px 30px;
`

const Title = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
`

const gradientSlide = keyframes`
    0% {background-position: 0%;}
    100% {background-position: -300%;}
`

const NewPill = styled.div`
    background: linear-gradient(to right, rgb(49, 145, 255), rgb(0, 255, 153), rgb(49, 145, 255)) 0% 100% / 300%;
    animation: ${gradientSlide} 5s linear 0s infinite;
    border-radius: 3.35px;
    height: 20px;
    width: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    & h4 {
        font-size: 10px;
        font-weight: bolder;
        color: ${giphyBlack};
    }
`

const Gif = styled.div`
    height: 224px;
    width: 326px;
    top: -76px;
    position: relative;
    & img {
        height: 224px;
        width: 326px;
        mask-image: linear-gradient(to left, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0));
    }
`

const StyledIconButton = styled(IconButton)`
    position: absolute;
    right: 4px;
    top: 78px;
`

const HireArtistBanner = () => {
    const [hasClosedBanner, setHasClosedBanner] = useLocalStorage('has-closed-artist-banner', false)
    const [isFadingOut, setIsFadingOut] = useState(false)
    const [showIt, setShowIt] = useState(false)
    useEffect(() => {
        let timeout: NodeJS.Timeout
        if (!hasClosedBanner) {
            timeout = setTimeout(() => {
                setShowIt(true)
            }, 0)
        }
        return () => clearTimeout(timeout)
    }, [hasClosedBanner])

    const handleClose = () => {
        setIsFadingOut(true)
    }

    const handleAnimationEnd = () => {
        if (isFadingOut) {
            setHasClosedBanner(true)
            setShowIt(false)
        }
    }

    return (
        <Banner $fadingOut={isFadingOut} onAnimationEnd={handleAnimationEnd} $showIt={showIt}>
            <ColorBar />
            <Info>
                <Title>
                    <h1>Hire A GIF Artist!</h1>
                    <NewPill>
                        <h4>New</h4>
                    </NewPill>
                </Title>
                <p>
                    {`Browse and hire GIF Artists for projects right from their GIPHY Channels.
                            Not sure how this works? Check out this`}{' '}
                    <a href="https://support.giphy.com/hc/en-us/articles/360022661172-How-to-Hire-A-GIF-Artist-on-GIPHY">
                        FAQ
                    </a>
                </p>
            </Info>
            <Gif>
                <img src={`/static/img/curious_internet.gif`} alt="Dog sipping from a coffee cup while on a laptop" />
                <StyledIconButton icon="close" inverted onClick={handleClose} size="small" />
            </Gif>
        </Banner>
    )
}

export default HireArtistBanner
